import {
  Component,
  OnInit,
  ViewChild,
  Output,
  Input,
  EventEmitter,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { WorkNotificationService } from '../../../services/work-notification.service';
import { SelectItem } from 'primeng/api';
import {
  WorkNotification,
  WorkNotificationWorkOrderCandidate,
} from '../../../models/work-notification.model';
import { cloneDeep } from 'lodash';
import { PagedRequest } from '../../../models/requests/paged-request';
import {
  ordersCandidatesFilterOptions,
  defaultOptions,
} from '../../../shared/constants/sanitization-options.constans';
import { Popover } from 'primeng/popover';
import { getScoreColorClass } from '../../../../utils/score-color.utils';
import {
  SanitizationFilterService,
  SHARED_FILTER_FIELDS,
  WORK_NOTIFICATION_FILTER_FIELDS,
} from '../../../services/sanitization-filter.service';

@Component({
  selector: 'app-work-notification',
  templateUrl: './work-notification.component.html',
  styleUrl: './work-notification.component.scss',
  standalone: false,
})
export class WorkNotificationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('workNotificationTable') workNotificationTable: any;
  @ViewChild('candidatePopover') candidatePopover!: Popover;
  @Input() plantOptions: SelectItem[] = defaultOptions;
  @Input() mainWorkCenterOptions: SelectItem[] = defaultOptions;
  @Output() totalRecordsChange = new EventEmitter<number>();

  private destroy$ = new Subject<void>();
  private tableInitialized = false;

  createdOnDate: Date | Date[] | null = null;
  workNotifications: WorkNotification[] = [];
  totalRecords = 0;
  loading = true;
  selectedNotification: WorkNotification | null = null;
  selectedCandidate: WorkNotificationWorkOrderCandidate | null = null;
  selectedPlant: string | null = null;
  selectedMainWorkCenters: string[] | null = null;
  selectedFunctionalLocation: string | null = null;
  initialFilters: any = {};
  ordersCandidatesFilterOptions: SelectItem[] = ordersCandidatesFilterOptions;
  hasActiveFilters = false;

  private readonly DEFAULT_FILTERS: Record<string, { value: any; matchMode: string }> = {
    [WORK_NOTIFICATION_FILTER_FIELDS.WORK_NOTIFICATION_ID]: { value: null, matchMode: 'contains' },
    [WORK_NOTIFICATION_FILTER_FIELDS.PLANT]: { value: null, matchMode: 'contains' },
    [WORK_NOTIFICATION_FILTER_FIELDS.MAIN_WORK_CENTER]: { value: null, matchMode: 'in' },
    [WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION]: { value: null, matchMode: 'contains' },
    [WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID]: { value: null, matchMode: 'contains' },
    [WORK_NOTIFICATION_FILTER_FIELDS.CREATED_ON]: { value: null, matchMode: 'equals' },
    [WORK_NOTIFICATION_FILTER_FIELDS.DESCRIPTION]: { value: null, matchMode: 'contains' },
    [WORK_NOTIFICATION_FILTER_FIELDS.CANDIDATES]: { value: null, matchMode: 'contains' },
  };

  constructor(
    private workNotificationService: WorkNotificationService,
    private filterService: SanitizationFilterService,
  ) {}

  ngOnInit() {
    this.restoreFilters();
    this.initialFilters = this.buildInitialFilters();
    this.subscribeToPlant();
    this.subscribeToMainWorkCenter();
    this.filterService.clearAll$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetTable());
  }

  private subscribeToPlant(): void {
    this.filterService.plant$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.selectedPlant = v;
      if (this.tableInitialized && !this.filterService.isClearing)
        this.applySharedFilterToTable(SHARED_FILTER_FIELDS.PLANT, v);
    });
  }

  private subscribeToMainWorkCenter(): void {
    this.filterService.mainWorkCenters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.selectedMainWorkCenters = v;
        if (this.tableInitialized && !this.filterService.isClearing)
          this.applySharedFilterToTable(
            SHARED_FILTER_FIELDS.MAIN_WORK_CENTER,
            v,
          );
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.tableInitialized = true;
    const hasAny =
      Object.values(this.filterService.getShared()).some((v) => v !== null) ||
      Object.values(this.filterService.getWorkNotification()).some(
        (v) => v !== null,
      );
    if (hasAny) setTimeout(() => this.workNotificationTable?._filter());
  }

  private buildInitialFilters(): any {
    const shared = this.filterService.getShared();
    const own = this.filterService.getWorkNotification();
    const filters: any = {};
    if (shared.plant)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.PLANT] = {
        value: shared.plant,
        matchMode: 'contains',
      };
    if (shared.mainWorkCenters?.length)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.MAIN_WORK_CENTER] = {
        value: shared.mainWorkCenters,
        matchMode: 'in',
      };
    if (shared.functionalLocation)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION] = {
        value: shared.functionalLocation,
        matchMode: 'contains',
      };
    if (own.functionalLocationId)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID] = {
        value: own.functionalLocationId,
        matchMode: 'contains',
      };
    if (own.workNotificationId)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.WORK_NOTIFICATION_ID] = {
        value: own.workNotificationId,
        matchMode: 'contains',
      };
    if (own.description)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.DESCRIPTION] = {
        value: own.description,
        matchMode: 'contains',
      };
    if (own.candidates !== null)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.CANDIDATES] = {
        value: own.candidates,
        matchMode: 'contains',
      };
    if (own.createdOn)
      filters[WORK_NOTIFICATION_FILTER_FIELDS.CREATED_ON] = {
        value: own.createdOn.split(',').map((d: string) => new Date(d)),
        matchMode: 'equals',
      };
    return filters;
  }

  private restoreFilters(): void {
    const shared = this.filterService.getShared();
    const own = this.filterService.getWorkNotification();
    this.selectedPlant = shared.plant;
    this.selectedMainWorkCenters = shared.mainWorkCenters;
    this.selectedFunctionalLocation = shared.functionalLocation;
    this.createdOnDate = own.createdOn
      ? own.createdOn.split(',').map((d) => new Date(d))
      : null;
  }

  private applySharedFilterToTable(
    field: string,
    value: string | string[] | null,
  ): void {
    if (!this.workNotificationTable) return;
    const matchMode = Array.isArray(value) ? 'in' : 'contains';
    this.workNotificationTable.filters = {
      ...this.workNotificationTable.filters,
      [field]: { value, matchMode },
    };
    this.workNotificationTable._filter();
  }

  onPlantFilterChange(value: string | null): void {
    this.filterService.setPlant(value);
  }

  onMainWorkCenterFilterChange(
    value: string[] | null,
    filterCallback: (v: any) => void,
  ): void {
    const normalizedValue = value && value.length > 0 ? value : null;
    this.filterService.setMainWorkCenters(normalizedValue);
    filterCallback(normalizedValue);
  }

  onRowSelect(workNotification: WorkNotification) {
    this.selectedNotification = workNotification;
  }

  getConfidenceScoreClass(score: number): string {
    return getScoreColorClass(score);
  }

  getParentWorkNotification(
    workNotificationId: string,
  ): WorkNotification | undefined {
    return this.workNotifications.find(
      (wn) => wn.SAP_WorkNotificationId === workNotificationId,
    );
  }

  onCandidateClick(
    candidate: WorkNotificationWorkOrderCandidate,
    event: Event,
  ) {
    event.stopPropagation();
    const isSameCandidate = this.selectedCandidate?.Uid === candidate.Uid;
    if (this.candidatePopover?.overlayVisible && isSameCandidate) {
      this.candidatePopover.hide();
      this.selectedCandidate = null;
    } else {
      if (this.candidatePopover?.overlayVisible) this.candidatePopover.hide();
      setTimeout(() => {
        this.selectedCandidate = candidate;
        this.candidatePopover?.show(event);
      }, 200);
    }
  }

  private isInvalidDateRange(filter: any): boolean {
    if (
      filter &&
      !Array.isArray(filter) &&
      filter.value !== null &&
      filter.value !== undefined
    ) {
      const dateRange = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      return !dateRange[0] || !dateRange[1];
    }
    return false;
  }

  private getFilterValue(filters: any, field: string): any {
    const f = filters[field];
    if (!f) return null;
    return Array.isArray(f) ? (f[0]?.value ?? null) : (f.value ?? null);
  }

  private serializeDateFilter(filter: any): string | null {
    if (!filter || this.isInvalidDateRange(filter)) return null;
    const val = Array.isArray(filter) ? filter[0]?.value : filter.value;
    if (!val) return null;
    const dates = Array.isArray(val) ? val : [val];
    return dates
      .filter(Boolean)
      .map((d: Date) => (d instanceof Date ? d.toISOString() : d))
      .join(',');
  }

  private computeHasActiveFilters(filters: any): boolean {
    return Object.values(WORK_NOTIFICATION_FILTER_FIELDS).some(
      (field) => this.getFilterValue(filters, field) !== null,
    );
  }

  onClearFilters(): void {
    this.filterService.clearAll();
  }

  private resetTable(): void {
    this.selectedPlant = null;
    this.selectedMainWorkCenters = null;
    this.selectedFunctionalLocation = null;
    this.createdOnDate = null;
    const clearedFilters = cloneDeep(this.DEFAULT_FILTERS);
    this.initialFilters = clearedFilters;
    if (this.workNotificationTable) {
      this.workNotificationTable.filters = clearedFilters;
      this.workNotificationTable.first = 0;
      this.workNotificationTable.sortField = null;
      this.workNotificationTable.sortOrder = 0;
      this.workNotificationTable._filter();
    }
  }

  loadData(lazyLoadEvent: TableLazyLoadEvent) {
    const filters = cloneDeep(lazyLoadEvent.filters ?? {});
    this.hasActiveFilters = this.computeHasActiveFilters(filters);

    this.filterService.persistShared({
      plant: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.PLANT,
      ),
      mainWorkCenters: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.MAIN_WORK_CENTER,
      ),
      functionalLocation: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION,
      ),
    });
    this.filterService.setWorkNotification({
      workNotificationId: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.WORK_NOTIFICATION_ID,
      ),
      description: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.DESCRIPTION,
      ),
      candidates: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.CANDIDATES,
      ),
      createdOn: this.serializeDateFilter(
        filters[WORK_NOTIFICATION_FILTER_FIELDS.CREATED_ON],
      ),
      functionalLocationId: this.getFilterValue(
        filters,
        WORK_NOTIFICATION_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID,
      ),
    });

    if (
      this.isInvalidDateRange(
        filters[WORK_NOTIFICATION_FILTER_FIELDS.CREATED_ON],
      )
    ) {
      delete filters[WORK_NOTIFICATION_FILTER_FIELDS.CREATED_ON];
    }

    this.loading = true;
    const payload: PagedRequest = {
      First: lazyLoadEvent.first ?? 0,
      Rows: lazyLoadEvent.rows ?? 10,
      SortField: lazyLoadEvent.sortField ?? null,
      SortOrder: lazyLoadEvent.sortOrder ?? 0,
      Filters: filters,
    };

    this.workNotificationService.getPaged(payload).subscribe({
      next: (response) => {
        this.workNotifications = response.Items;
        this.totalRecords = response.TotalRecords;
        this.totalRecordsChange.emit(this.totalRecords);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading work notifications:', error);
        this.loading = false;
      },
    });
  }
}
